import type { Household } from './Household';

/**
 * The license: who is on it (owner + carers, each with a name and avatar),
 * who has the dog checked out right now, and short messages between them.
 * Presence is polled: every 20 s while you hold her, every 10 s while you
 * watch. Nudges ride the same heartbeat.
 */
export interface Member {
  device: string;
  name: string;
  avatar: string;
  role: 'owner' | 'carer';
  last_seen: number;
}
export interface Holder {
  device: string;
  name: string;
  avatar: string;
  since: number;
}
export interface Message {
  from: { name: string; avatar: string };
  text: string;
  at: number;
}

/** people first (this is who's looking after her), a few fun ones after */
export const AVATARS = [
  '👩', '👨', '🧑', '👧', '👦', '👶', '🧒', '🧓',
  '👩‍🦰', '👨‍🦰', '👩‍🦱', '👨‍🦱', '👱‍♀️', '👱', '👩‍🦳', '👨‍🦳', '👩‍🦲', '👨‍🦲', '🧔', '🧔‍♀️', '👵', '👴',
  '👩🏽', '👨🏽', '👩🏾', '👨🏾', '👩🏻', '👨🏻', '🧑🏿', '👧🏽', '👦🏾',
  '👩‍🎓', '🧑‍🎓', '👩‍⚕️', '👨‍💻', '👩‍🏫', '🧑‍🚀', '🧙', '🦸',
  '🐾', '🦴', '🎾', '🌙', '⭐', '🌸', '🦋', '🔥',
];
const DEVICE_KEY = 'lunaloo.device';
const ME_KEY = 'lunaloo.me';

export class License {
  readonly device: string;
  me: { name: string; avatar: string; role?: string } | null = null;
  holder: Holder | null = null;
  onHolderChange?: (holder: Holder | null, mine: boolean) => void;
  onMessage?: (m: Message) => void;
  private timer = 0;
  private lastInput = performance.now();
  private wantHer = true;

  constructor(private hh: Household) {
    let d = localStorage.getItem(DEVICE_KEY);
    if (!d) {
      d = (crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2) + Date.now().toString(36)).slice(0, 36);
      localStorage.setItem(DEVICE_KEY, d);
    }
    this.device = d;
    try {
      this.me = JSON.parse(localStorage.getItem(ME_KEY) ?? 'null');
    } catch {
      this.me = null;
    }
  }

  get mine(): boolean {
    return !!this.holder && this.holder.device === this.device;
  }

  private async api(path: string, init?: RequestInit): Promise<Response> {
    const res = await fetch(`https://api.lunaloo.net${path}`, { ...init, headers: { 'Content-Type': 'application/json' } });
    if (!res.ok) throw new Error(`${res.status}`);
    return res;
  }

  /** this device is already a member (e.g. after switching licenses on an account): adopt that record */
  async resolveMe(): Promise<boolean> {
    if (this.me || !this.hh.code) return !!this.me;
    try {
      const list = await this.members();
      const mine = list.find((m) => m.device === this.device);
      if (!mine) return false;
      this.me = { name: mine.name, avatar: mine.avatar, role: mine.role };
      localStorage.setItem(ME_KEY, JSON.stringify(this.me));
      return true;
    } catch {
      return false;
    }
  }

  /** register this phone on the license (idempotent; updates name/avatar) */
  async join(name: string, avatar: string): Promise<boolean> {
    const code = await this.hh.ensureCode();
    if (!code) return false;
    try {
      const r = (await (await this.api(`/v1/households/${code}/members`, { method: 'POST', body: JSON.stringify({ device: this.device, name, avatar }) })).json()) as { role: string };
      this.me = { name, avatar, role: r.role };
      localStorage.setItem(ME_KEY, JSON.stringify(this.me));
      await this.hh.setName(this.hh.name || name);
      return true;
    } catch {
      return false;
    }
  }

  async members(): Promise<Member[]> {
    if (!this.hh.code) return [];
    try {
      return ((await (await this.api(`/v1/households/${this.hh.code}/members`)).json()) as { members: Member[] }).members;
    } catch {
      return [];
    }
  }

  /** hand the license to another member (owner only) */
  async makeOwner(device: string): Promise<boolean> {
    if (!this.hh.code) return false;
    try {
      await this.api(`/v1/households/${this.hh.code}/members/${device}/owner?by=${this.device}`, { method: 'POST' });
      if (this.me) {
        this.me = { ...this.me, role: 'carer' };
        localStorage.setItem(ME_KEY, JSON.stringify(this.me));
      }
      return true;
    } catch {
      return false;
    }
  }

  /** delete the household outright (owner only): every carer loses her */
  async deleteHousehold(): Promise<boolean> {
    if (!this.hh.code) return false;
    try {
      await this.api(`/v1/households/${this.hh.code}?by=${this.device}`, { method: 'DELETE' });
      return true;
    } catch {
      return false;
    }
  }

  async remove(device: string): Promise<boolean> {
    if (!this.hh.code) return false;
    try {
      await this.api(`/v1/households/${this.hh.code}/members/${device}?by=${this.device}`, { method: 'DELETE' });
      return true;
    } catch {
      return false;
    }
  }

  /** call on any user interaction: keeps the check-out alive */
  touch(): void {
    this.lastInput = performance.now();
  }

  /** start heartbeating; `wantHer` false means just watch (after handing over) */
  start(): void {
    this.stop();
    const tick = async (): Promise<void> => {
      await this.beat();
      this.timer = window.setTimeout(() => void tick(), this.mine ? 20_000 : 10_000);
    };
    void tick();
    window.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') void this.release();
      else void this.beat();
    });
  }

  stop(): void {
    window.clearTimeout(this.timer);
  }

  private async beat(): Promise<void> {
    if (!this.hh.code || document.visibilityState === 'hidden') return;
    if (!this.wantHer && !this.mine) {
      // watching: only poll who has her and pick up messages
    }
    const active = performance.now() - this.lastInput < 60_000 ? 1 : 0;
    try {
      const r = (await (
        await this.api(`/v1/households/${this.hh.code}/dogs/${this.hh.dog}/presence?active=${active}`, { method: 'POST', body: JSON.stringify({ device: this.device }) })
      ).json()) as { holder: Holder; you: boolean; messages: Message[] };
      const was = this.holder?.device;
      this.holder = r.holder;
      if (was !== r.holder.device) this.onHolderChange?.(r.holder, r.you);
      for (const m of r.messages) this.onMessage?.(m);
    } catch {
      /* offline: keep whatever we had */
    }
  }

  /** hand her over: let go, and don't grab her back on the next beat for a while */
  async release(): Promise<void> {
    if (!this.hh.code) return;
    try {
      await this.api(`/v1/households/${this.hh.code}/dogs/${this.hh.dog}/release`, { method: 'POST', body: JSON.stringify({ device: this.device }) });
    } catch {
      /* ignore */
    }
    this.holder = null;
    this.wantHer = false;
    this.onHolderChange?.(null, false);
    window.setTimeout(() => (this.wantHer = true), 30_000);
  }

  /** ask for her / say something to whoever has her */
  async nudge(text: string): Promise<boolean> {
    if (!this.hh.code) return false;
    try {
      await this.api(`/v1/households/${this.hh.code}/dogs/${this.hh.dog}/nudge`, { method: 'POST', body: JSON.stringify({ device: this.device, text }) });
      return true;
    } catch {
      return false;
    }
  }
}
